import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { adminTicketingLinksApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { appNav } from "../../routes/appNavigation";
import type {
  AdminCompanyEvent,
  TicketingCatalogCategory,
  TicketingCatalogEvent,
  TicketingLink,
  TicketingTierMapping,
} from "../../types";
import EventSearchSelect from "./EventSearchSelect";
import "./TicketingLinksAdminPage.css";

type AdminCompanyTier = AdminCompanyEvent["ticket_tiers"][number];

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function suggestMappings(
  tiers: AdminCompanyEvent["ticket_tiers"],
  categories: TicketingCatalogCategory[]
): TicketingTierMapping[] {
  const mappings: TicketingTierMapping[] = [];
  const unused = new Set(tiers.map((t: AdminCompanyTier) => t.id));
  for (const cat of categories) {
    const catKey = normalizeName(cat.name);
    const byName = tiers.find(
      (t: AdminCompanyTier) => unused.has(t.id) && normalizeName(t.tier_name) === catKey
    );
    if (byName) {
      mappings.push({ ticketing_category_id: cat.id, ticket_tier_id: byName.id });
      unused.delete(byName.id);
      continue;
    }
    const byPrice = tiers.find(
      (t: AdminCompanyTier) =>
        unused.has(t.id) && Math.abs(Number(t.price) - Number(cat.price)) < 0.01
    );
    if (byPrice) {
      mappings.push({ ticketing_category_id: cat.id, ticket_tier_id: byPrice.id });
      unused.delete(byPrice.id);
    }
  }
  if (mappings.length === 0 && categories.length === 1 && tiers.length === 1) {
    mappings.push({
      ticketing_category_id: categories[0].id,
      ticket_tier_id: tiers[0].id,
    });
  }
  return mappings;
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  return value.slice(0, 10);
}

const TicketingLinksAdminPage: React.FC = () => {
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const [eventId, setEventId] = useState("");
  const [ticketingEventId, setTicketingEventId] = useState("");

  const osEventsQuery = useQuery({
    queryKey: ["adminTicketingLinks", "eventOsEvents"],
    queryFn: (): Promise<AdminCompanyEvent[]> =>
      adminTicketingLinksApi.eventOsEvents(undefined, token ?? undefined),
    enabled: Boolean(token && user?.is_system_admin),
  });

  const ticketingEventsQuery = useQuery({
    queryKey: ["adminTicketingLinks", "ticketingEvents"],
    queryFn: (): Promise<TicketingCatalogEvent[]> =>
      adminTicketingLinksApi.ticketingEvents(undefined, token ?? undefined),
    enabled: Boolean(token && user?.is_system_admin),
  });

  const linksQuery = useQuery({
    queryKey: ["adminTicketingLinks", "list"],
    queryFn: (): Promise<TicketingLink[]> => adminTicketingLinksApi.list(token ?? undefined),
    enabled: Boolean(token && user?.is_system_admin),
  });

  const osEvents: AdminCompanyEvent[] = osEventsQuery.data ?? [];
  const ticketingEvents: TicketingCatalogEvent[] = ticketingEventsQuery.data ?? [];
  const selectedEvent = useMemo(
    () => osEvents.find((event) => event.id === eventId) ?? null,
    [osEvents, eventId]
  );
  const selectedTicketingEvent = useMemo(
    () => ticketingEvents.find((event) => String(event.id) === ticketingEventId) ?? null,
    [ticketingEvents, ticketingEventId]
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEvent || !selectedTicketingEvent) {
        throw new Error("Select both events");
      }
      const tier_mappings = suggestMappings(
        selectedEvent.ticket_tiers,
        selectedTicketingEvent.seat_categories || []
      );
      // Activity events (sold by activity + slot) have no seat categories to map.
      if (tier_mappings.length === 0 && !selectedTicketingEvent.has_activities) {
        throw new Error("Could not match ticket categories to Event OS tiers");
      }
      return adminTicketingLinksApi.create(
        {
          event_id: selectedEvent.id,
          ticketing_event_id: selectedTicketingEvent.id,
          tier_mappings,
          backfill: true,
        },
        token ?? undefined
      );
    },
    onSuccess: () => {
      toast.success("Events linked. Existing paid bookings will sync shortly.");
      queryClient.invalidateQueries({ queryKey: ["adminTicketingLinks"] });
      setEventId("");
      setTicketingEventId("");
    },
    onError: (err: Error) => toast.error(err.message || "Could not create link"),
  });

  const unlinkMutation = useMutation({
    mutationFn: (linkId: string) => adminTicketingLinksApi.unlink(linkId, token ?? undefined),
    onSuccess: () => {
      toast.success("Link removed. Historical sales were kept.");
      queryClient.invalidateQueries({ queryKey: ["adminTicketingLinks"] });
    },
    onError: (err: Error) => toast.error(err.message || "Could not unlink"),
  });

  const backfillMutation = useMutation({
    mutationFn: (linkId: string) => adminTicketingLinksApi.backfill(linkId, token ?? undefined),
    onSuccess: () => toast.success("Backfill queued"),
    onError: (err: Error) => toast.error(err.message || "Backfill failed"),
  });

  if (!user?.is_system_admin) {
    return <Navigate to={appNav.events()} replace />;
  }

  const activeLinks: TicketingLink[] = (linksQuery.data ?? []).filter(
    (link: TicketingLink) => link.status === "active"
  );

  return (
    <div className="ticketing-links-admin">
      <header className="ticketing-links-admin__header">
        <div>
          <h1>Zoniq.in event links</h1>
          <p className="eventos-subtle">
            Pick the two events. Search as you type, then confirm the details before linking.
          </p>
        </div>
      </header>

      <section className="ticketing-links-admin__card">
        <h2>Create link</h2>
        <div className="ticketing-links-admin__grid">
          <EventSearchSelect
            label="Ticketing event"
            value={ticketingEventId}
            loading={ticketingEventsQuery.isLoading}
            placeholder="Search zoniq.in events…"
            onChange={setTicketingEventId}
            options={ticketingEvents.map((event) => ({
              value: String(event.id),
              label: `${event.title} (#${event.id})`,
              searchText: [
                event.title,
                event.slug,
                event.organizer_name || "",
                event.city || "",
                event.status,
                String(event.id),
              ].join(" "),
            }))}
          />
          <EventSearchSelect
            label="Event OS event"
            value={eventId}
            loading={osEventsQuery.isLoading}
            placeholder="Search Event OS events…"
            onChange={setEventId}
            options={osEvents.map((event) => ({
              value: event.id,
              label: event.name,
              searchText: [
                event.name,
                event.city,
                event.company_name || "",
                event.owner_name || "",
                event.owner_email || "",
                event.artist || "",
                event.status,
              ].join(" "),
            }))}
          />
        </div>

        {selectedTicketingEvent || selectedEvent ? (
          <div className="ticketing-links-admin__preview">
            <div>
              <h3>Ticketing event</h3>
              {selectedTicketingEvent ? (
                <dl className="ticketing-links-admin__meta">
                  <div>
                    <dt>Title</dt>
                    <dd>{selectedTicketingEvent.title}</dd>
                  </div>
                  <div>
                    <dt>Organizer</dt>
                    <dd>{selectedTicketingEvent.organizer_name || "—"}</dd>
                  </div>
                  <div>
                    <dt>City</dt>
                    <dd>{selectedTicketingEvent.city || "—"}</dd>
                  </div>
                  <div>
                    <dt>Date</dt>
                    <dd>{formatDate(selectedTicketingEvent.event_date)}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>{selectedTicketingEvent.status}</dd>
                  </div>
                  <div>
                    <dt>Seats</dt>
                    <dd>
                      {selectedTicketingEvent.available_seats}/{selectedTicketingEvent.total_seats}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p className="eventos-subtle">Select a ticketing event</p>
              )}
            </div>
            <div>
              <h3>Event OS event</h3>
              {selectedEvent ? (
                <dl className="ticketing-links-admin__meta">
                  <div>
                    <dt>Name</dt>
                    <dd>{selectedEvent.name}</dd>
                  </div>
                  <div>
                    <dt>Company</dt>
                    <dd>{selectedEvent.company_name || "—"}</dd>
                  </div>
                  <div>
                    <dt>User</dt>
                    <dd>
                      {selectedEvent.owner_name || "—"}
                      {selectedEvent.owner_email ? ` · ${selectedEvent.owner_email}` : ""}
                    </dd>
                  </div>
                  <div>
                    <dt>City</dt>
                    <dd>{selectedEvent.city}</dd>
                  </div>
                  <div>
                    <dt>Date</dt>
                    <dd>{formatDate(selectedEvent.event_date)}</dd>
                  </div>
                  <div>
                    <dt>Venue</dt>
                    <dd>{selectedEvent.venue_name || "—"}</dd>
                  </div>
                </dl>
              ) : (
                <p className="eventos-subtle">Select an Event OS event</p>
              )}
            </div>
          </div>
        ) : null}

        {selectedEvent && selectedTicketingEvent ? (
          <>
            {selectedTicketingEvent.has_activities ? (
              <p className="eventos-subtle">
                This event is sold by activity and time slot, so there are no seat categories to map. Its
                sales sync as activity tickets and appear under Tickets → Activity sales.
              </p>
            ) : null}
            <button
              type="button"
              className="eventos-primary-btn ticketing-links-admin__save"
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? "Linking…" : "Save link + backfill"}
            </button>
          </>
        ) : null}
      </section>

      <section className="ticketing-links-admin__card">
        <h2>Existing links</h2>
        {linksQuery.isLoading ? <p className="eventos-subtle">Loading…</p> : null}
        <div className="ticketing-links-admin__links">
          {activeLinks.map((link) => (
            <div key={link.id} className="ticketing-links-admin__link-row">
              <div>
                <strong>{link.event_name}</strong>
                <span className="eventos-subtle">
                  {" "}
                  · {link.company_name} · zoniq.in #{link.ticketing_event_id}
                </span>
              </div>
              <div className="ticketing-links-admin__link-actions">
                <button
                  type="button"
                  className="eventos-nav-btn"
                  onClick={() => backfillMutation.mutate(link.id)}
                  disabled={backfillMutation.isPending}
                >
                  Backfill
                </button>
                <button
                  type="button"
                  className="eventos-nav-btn"
                  onClick={() => unlinkMutation.mutate(link.id)}
                  disabled={unlinkMutation.isPending}
                >
                  Unlink
                </button>
              </div>
            </div>
          ))}
          {activeLinks.length === 0 ? (
            <p className="eventos-subtle">No active links yet.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default TicketingLinksAdminPage;
